import React, { useEffect, useState } from "react";
import { Spin } from "antd";
import moment from "moment";
import { getCustomFormResponse } from "../../services/formService";
import DrawerComponent from "../../components/drawer";
import FormResponse from "./FormResponse";
import toast from "react-hot-toast";
import logger from '../../utils/logger';
import styled from "styled-components";

interface FormResponsesListProps {
  formId: string;
  formStructure?: any;
}

const FormResponsesList: React.FC<FormResponsesListProps> = ({ formId, formStructure }) => {
  const [responses, setResponses] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedResponse, setSelectedResponse] = useState<any>(null);
  const [showResponseDrawer, setShowResponseDrawer] = useState<boolean>(false);
  const [totalResponses, setTotalResponses] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage] = useState<number>(10);

  useEffect(() => {
    if (formId) {
      loadResponses();
    }
  }, [formId]);

  const loadResponses = async () => {
    try {
      setIsLoading(true);
      const response = await getCustomFormResponse(formId);
      setIsLoading(false);

      if (response && response.status) {
        setResponses(response.data.data || []);
        setTotalResponses(response.data.total || 0);
        logger.log('Form responses loaded:', response.data);
      }
    } catch (err) {
      setIsLoading(false);
      logger.log('Error loading form responses:', err);
      toast.error("Failed to load responses");
    }
  };

  const handleResponseClick = (responseData: any) => {
    setSelectedResponse(responseData);
    setShowResponseDrawer(true);
  };

  const closeResponseDrawer = () => {
    setShowResponseDrawer(false);
    setSelectedResponse(null);
  };

  /**
   * Downloads responses as CSV file
   */
  const downloadCSV = () => {
    if (!responses || responses.length === 0) {
      toast.error("No responses to download");
      return;
    }

    try {
      // Flatten all responses first (handle both nested and flat structures)
      const flattenedResponses = responses.map((response) => {
        const flatData: any = {};

        if (response.formData) {
          Object.entries(response.formData).forEach(([key, value]) => {
            // Skip system fields
            if (['createdBy', 'integration', 'organization', 'list', '_id'].includes(key)) return;

            // Check if this is a question_N key with nested group data (NEW FORMAT)
            if (key.startsWith('question_') && typeof value === 'object' && value !== null && !Array.isArray(value)) {
              // This is a nested question object, extract group fields
              Object.entries(value).forEach(([groupName, groupData]) => {
                if (typeof groupData === 'object' && groupData !== null && !Array.isArray(groupData)) {
                  // Flatten group fields
                  Object.entries(groupData).forEach(([fieldName, fieldValue]) => {
                    flatData[fieldName] = fieldValue;
                  });
                }
              });
            } else {
              // Regular field at root level OR OLD FORMAT (Group_Field_1_field_1, etc.)
              flatData[key] = value;
            }
          });
        }

        return {
          ...response,
          flatFormData: flatData
        };
      });

      // Collect all unique field names from all flattened responses
      const allFields = new Set<string>();
      flattenedResponses.forEach((response) => {
        Object.keys(response.flatFormData).forEach((key) => {
          allFields.add(key);
        });
      });

      // Convert to array and sort alphabetically
      const fieldNames = Array.from(allFields).sort();

      // Add metadata columns at the beginning
      const headers = ['#', 'Submitted On', ...fieldNames.map(field =>
        field.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
      )];

      // Create CSV rows
      const csvRows = [headers.join(',')];

      flattenedResponses.forEach((response, index) => {
        const row: string[] = [];

        // Add row number
        row.push(`${index + 1}`);

        // Add submission date
        row.push(`"${moment(response.createdAt).format('DD MMM YYYY hh:mm A')}"`);

        // Add field values from flattened data
        fieldNames.forEach((fieldName) => {
          let value = response.flatFormData?.[fieldName] || '';

          // Handle arrays (for file uploads or multi-select)
          if (Array.isArray(value)) {
            value = value.join('; ');
          }

          // Escape quotes and wrap in quotes if contains comma, quote, or newline
          value = String(value).replace(/"/g, '""');
          if (value.includes(',') || value.includes('"') || value.includes('\n')) {
            value = `"${value}"`;
          }

          row.push(value);
        });

        csvRows.push(row.join(','));
      });

      // Create blob and download
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `form-responses-${moment().format('YYYY-MM-DD-HHmmss')}.csv`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Downloaded ${responses.length} responses`);
    } catch (error) {
      logger.log('CSV download error:', error);
      toast.error("Failed to download CSV");
    }
  };

  return (
    <Root>
      <div className="responses-container">
        <div className="header-section">
          <div className="title-section">
            <h2 className="page-title">
              <span className="icon">📊</span>
              Form Responses
            </h2>
            <span className="count-badge">{totalResponses} {totalResponses === 1 ? 'response' : 'responses'}</span>
          </div>

          {totalResponses > 0 && (
            <div className="action-buttons">
              <button className="btn-secondary" onClick={loadResponses} disabled={isLoading}>
                <span className="btn-icon">🔄</span>
                Refresh
              </button>
              <button className="btn-primary" onClick={downloadCSV} disabled={isLoading}>
                <span className="btn-icon">📥</span>
                Download CSV
              </button>
            </div>
          )}
        </div>

        <Spin size="large" tip="Loading responses..." spinning={isLoading}>
          {totalResponses === 0 && !isLoading ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h3 className="empty-title">No responses yet</h3>
              <p className="empty-description">
                Responses will appear here once users submit your form
              </p>
            </div>
          ) : (
            <div className="table-container">
              <table className="responses-table">
                <thead>
                  <tr>
                    <th className="col-number">#</th>
                    <th className="col-name">Submitted By</th>
                    <th className="col-contact">Email/Phone</th>
                    <th className="col-date">Submitted On</th>
                    <th className="col-status">Status</th>
                    <th className="col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {responses
                    .slice((currentPage - 1) * perPage, currentPage * perPage)
                    .map((response: any, globalIndex: number) => {
                      const index = (currentPage - 1) * perPage + globalIndex;

                      // Flatten the response data for display (handle both formats)
                      const flatData: any = {};
                      if (response.formData) {
                        Object.entries(response.formData).forEach(([key, value]) => {
                          // NEW FORMAT: question_N with nested groups
                          if (key.startsWith('question_') && typeof value === 'object' && value !== null && !Array.isArray(value)) {
                            Object.entries(value).forEach(([groupName, groupData]) => {
                              if (typeof groupData === 'object' && groupData !== null && !Array.isArray(groupData)) {
                                Object.entries(groupData).forEach(([fieldName, fieldValue]) => {
                                  flatData[fieldName] = fieldValue;
                                });
                              }
                            });
                          } else {
                            // Regular field OR OLD FORMAT (Group_Field_1_field_1)
                            flatData[key] = value;
                          }
                        });
                      }

                      return (
                    <tr key={response._id || index} className="table-row">
                      <td className="col-number">
                        <span className="row-number">{index + 1}</span>
                      </td>
                      <td className="col-name">
                        <div className="name-cell">
                          <span
                            className="name-text"
                            title={flatData?.name ||
                                   flatData?.full_name ||
                                   flatData?.Name ||
                                   flatData?.Full_Name ||
                                   'Anonymous'}
                          >
                            {flatData?.name ||
                             flatData?.full_name ||
                             flatData?.Name ||
                             flatData?.Full_Name ||
                             'Anonymous'}
                          </span>
                        </div>
                      </td>
                      <td className="col-contact">
                        <span
                          className="contact-text"
                          title={flatData?.email ||
                                 flatData?.Email ||
                                 flatData?.phone ||
                                 flatData?.Phone ||
                                 'N/A'}
                        >
                          {flatData?.email ||
                           flatData?.Email ||
                           flatData?.phone ||
                           flatData?.Phone ||
                           'N/A'}
                        </span>
                      </td>
                      <td className="col-date">
                        <div className="date-cell">
                          <span className="date-text">
                            {moment(response.createdAt).format("DD MMM, YYYY")}
                          </span>
                          <span className="time-text">
                            {moment(response.createdAt).format("hh:mm A")}
                          </span>
                        </div>
                      </td>
                      <td className="col-status">
                        <span className="status-badge status-submitted">
                          Submitted
                        </span>
                      </td>
                      <td className="col-actions">
                        <button
                          className="btn-view"
                          onClick={() => handleResponseClick(response)}
                        >
                          <span className="btn-icon">👁️</span>
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Controls */}
          {totalResponses > perPage && (
            <div className="pagination-container">
              <div className="pagination-info">
                Showing {Math.min((currentPage - 1) * perPage + 1, totalResponses)} to{" "}
                {Math.min(currentPage * perPage, totalResponses)} of {totalResponses} responses
              </div>
              <div className="pagination-controls">
                <button
                  className="page-btn"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </button>
                <button
                  className="page-btn"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span className="page-numbers">
                  Page {currentPage} of {Math.ceil(totalResponses / perPage)}
                </span>
                <button
                  className="page-btn"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage >= Math.ceil(totalResponses / perPage)}
                >
                  Next
                </button>
                <button
                  className="page-btn"
                  onClick={() => setCurrentPage(Math.ceil(totalResponses / perPage))}
                  disabled={currentPage >= Math.ceil(totalResponses / perPage)}
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </Spin>
      </div>

      {/* Response Details Drawer */}
      <DrawerComponent
        openWithHeader={showResponseDrawer}
        setOpenWithHeader={setShowResponseDrawer}
        drawerTitle="Response Details"
        size="md"
      >
        {selectedResponse && (
          <FormResponse
            open={true}
            handleClose={closeResponseDrawer}
            formDetails={selectedResponse}
            formFields={formStructure?.form || []}
          />
        )}
      </DrawerComponent>
    </Root>
  );
};

export default FormResponsesList;

const Root = styled.div`
  .responses-container {
    background: white;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  }

  .header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;
  }

  .title-section {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: #111827;
    margin: 0;
    font-family: 'Gilroy-Bold', 'Gilroy', sans-serif;
    display: flex;
    align-items: center;
    gap: 8px;

    .icon {
      font-size: 28px;
    }
  }

  .count-badge {
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
    color: white;
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    font-family: 'Gilroy-SemiBold', 'Gilroy', sans-serif;
  }

  .action-buttons {
    display: flex;
    gap: 12px;
  }

  .btn-primary,
  .btn-secondary {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    font-family: 'Gilroy-SemiBold', 'Gilroy', sans-serif;

    .btn-icon {
      font-size: 16px;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .btn-primary {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
    box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, #059669 0%, #047857 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(16, 185, 129, 0.4);
    }
  }

  .btn-secondary {
    background: #f3f4f6;
    color: #374151;
    border: 1px solid #d1d5db;

    &:hover:not(:disabled) {
      background: #e5e7eb;
      border-color: #9ca3af;
    }
  }

  .empty-state {
    text-align: center;
    padding: 80px 20px;
    color: #6b7280;
  }

  .empty-icon {
    font-size: 64px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  .empty-title {
    font-size: 20px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
    font-family: 'Gilroy-SemiBold', 'Gilroy', sans-serif;
  }

  .empty-description {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
  }

  .table-container {
    overflow-x: auto;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
  }

  .responses-table {
    width: 100%;
    border-collapse: collapse;
    font-family: 'Gilroy-Regular', 'Gilroy', sans-serif;
    table-layout: fixed;

    thead {
      background: linear-gradient(135deg, #f8f9fa 0%, #f1f3f5 100%);
      border-bottom: 2px solid #dee2e6;

      tr {
        th {
          padding: 16px 12px;
          text-align: left;
          font-weight: 600;
          color: #495057;
          font-size: 13px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-family: 'Gilroy-SemiBold', 'Gilroy', sans-serif;
          white-space: nowrap;

          &.col-number {
            width: 50px;
            max-width: 50px;
          }

          &.col-name {
            width: 150px;
            max-width: 150px;
          }

          &.col-contact {
            width: 180px;
            max-width: 180px;
          }

          &.col-date {
            width: 140px;
            max-width: 140px;
          }

          &.col-status {
            width: 110px;
            max-width: 110px;
          }

          &.col-actions {
            width: 130px;
            max-width: 130px;
          }
        }
      }
    }

    tbody {
      tr.table-row {
        border-bottom: 1px solid #f1f3f4;
        transition: all 0.2s ease;

        &:hover {
          background-color: #f8f9fa;
          transform: scale(1.001);
        }

        &:last-child {
          border-bottom: none;
        }

        td {
          padding: 16px 12px;
          color: #374151;
          font-size: 14px;
          vertical-align: middle;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .col-number {
          width: 50px;
          max-width: 50px;

          .row-number {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
            background: #e5e7eb;
            border-radius: 50%;
            font-weight: 600;
            font-size: 13px;
            color: #6b7280;
          }
        }

        .col-name {
          width: 150px;
          max-width: 150px;
        }

        .col-contact {
          width: 180px;
          max-width: 180px;
        }

        .col-date {
          width: 140px;
          max-width: 140px;
          white-space: normal;
        }

        .col-status {
          width: 110px;
          max-width: 110px;
        }

        .col-actions {
          width: 130px;
          max-width: 130px;
        }

        .name-cell {
          .name-text {
            font-weight: 500;
            color: #111827;
            font-size: 14px;
            display: block;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }

        .contact-text {
          color: #6b7280;
          font-size: 13px;
          display: block;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .date-cell {
          display: flex;
          flex-direction: column;
          gap: 2px;

          .date-text {
            font-weight: 500;
            color: #374151;
            font-size: 13px;
          }

          .time-text {
            font-size: 12px;
            color: #9ca3af;
          }
        }

        .status-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.3px;

          &.status-submitted {
            background: #d4edda;
            color: #155724;
          }
        }

        .btn-view {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 13px;
          cursor: pointer;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s ease;
          box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);

          .btn-icon {
            font-size: 14px;
          }

          &:hover {
            background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(59, 130, 246, 0.4);
          }

          &:active {
            transform: translateY(0);
          }
        }
      }
    }
  }

  .pagination-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px;
    border-top: 1px solid #e5e7eb;
    margin-top: 20px;
    flex-wrap: wrap;
    gap: 16px;
  }

  .pagination-info {
    font-size: 14px;
    color: #6b7280;
    font-family: 'Gilroy-Medium', 'Gilroy', sans-serif;
  }

  .pagination-controls {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .page-btn {
    padding: 8px 14px;
    border: 1px solid #d1d5db;
    background: white;
    color: #374151;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: 'Gilroy-SemiBold', 'Gilroy', sans-serif;

    &:hover:not(:disabled) {
      background: #f3f4f6;
      border-color: #9ca3af;
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  .page-numbers {
    padding: 8px 12px;
    font-size: 13px;
    font-weight: 600;
    color: #1f2937;
    font-family: 'Gilroy-SemiBold', 'Gilroy', sans-serif;
  }

  /* Responsive Design */
  @media (max-width: 1024px) {
    .responses-container {
      padding: 20px;
    }

    .page-title {
      font-size: 20px;
    }

    .count-badge {
      font-size: 12px;
      padding: 4px 10px;
    }
  }

  @media (max-width: 768px) {
    .header-section {
      flex-direction: column;
      align-items: flex-start;
    }

    .action-buttons {
      width: 100%;

      button {
        flex: 1;
      }
    }

    .pagination-container {
      flex-direction: column;
      align-items: flex-start;
      padding: 16px;
    }

    .pagination-controls {
      width: 100%;
      justify-content: center;
    }

    .page-btn {
      flex: 1;
      padding: 6px 10px;
      font-size: 12px;
    }

    .page-numbers {
      font-size: 12px;
    }

    .table-container {
      border: none;
    }

    .responses-table {
      thead {
        display: none;
      }

      tbody {
        tr.table-row {
          display: block;
          margin-bottom: 16px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 16px;

          &:hover {
            transform: none;
          }

          td {
            display: block;
            padding: 8px 0;
            border: none;
            text-align: left;

            &::before {
              content: attr(data-label);
              font-weight: 600;
              color: #6b7280;
              display: block;
              margin-bottom: 4px;
              font-size: 12px;
              text-transform: uppercase;
            }

            &.col-number {
              display: none;
            }

            &.col-actions {
              margin-top: 12px;
              padding-top: 12px;
              border-top: 1px solid #e5e7eb;
            }
          }

          .btn-view {
            width: 100%;
            justify-content: center;
          }
        }
      }
    }
  }
`;
